import s_homePreview from "./assets/spring/h_preview.jpg"
import s_lockPreview from "./assets/spring/l_preview.jpg"
import s_homeScreen from "./assets/spring/h_screen.png"
import s_lockScreen from "./assets/spring/l_screen.png"


import summer_homePreview from "./assets/summer/h_preview.jpg"
import summer_lockPreview from "./assets/summer/l_preview.jpg"
import summer_homeScreen from "./assets/summer/h_screen.png"
import summer_lockScreen from "./assets/summer/l_screen.png"

import { motion } from "framer-motion"
import type { CSSProperties, MouseEvent } from "react"

interface DownloadViewProps {
    type?: string;
}

const MotionButton = motion.create("button")

const getImageFileName = (themeType: string, screenType: string, imageUrl: string) => {
    const extension = imageUrl.split("?")[0].match(/\.(png|jpe?g|webp)$/i)?.[1] || "png"

    return `dodu-${themeType}-${screenType}.${extension}`
}

const getImageFile = async (imageUrl: string, fileName: string) => {
    const response = await fetch(imageUrl)
    const blob = await response.blob()

    return new File([blob], fileName, { type: blob.type || "image/png" })
}

const downloadFile = (file: File) => {
    const objectUrl = URL.createObjectURL(file)
    const link = document.createElement("a")

    link.href = objectUrl
    link.download = file.name
    document.body.appendChild(link)
    link.click()
    link.remove()

    URL.revokeObjectURL(objectUrl)
}

const DownloadView = ({ type = "spring" }: DownloadViewProps) => {
    const assets: Record<string, { home: string; lock: string, isAvailable: boolean, deepLink: string, homeDownload: string, lockDownload: string }> = {
        spring: {
            home: s_homePreview, lock: s_lockPreview, isAvailable: true, deepLink: "https://apps.samsung.com/theme/ProductDetail.as?appId=com.SamTheme_DODU_Spring&from=GBadge_01_8845021_tag",
            homeDownload: s_homeScreen, lockDownload: s_lockScreen
        },
        summer: {
            home: summer_homePreview, lock: summer_lockPreview, isAvailable: false, deepLink: "",
            homeDownload: summer_homeScreen, lockDownload: summer_lockScreen
        },
        autumn: {
            home: "", lock: "", isAvailable: false, deepLink: "",
            homeDownload: "", lockDownload: ""
        },
        winter: {
            home: "", lock: "", isAvailable: false, deepLink: "",
            homeDownload: "", lockDownload: ""
        },
        night: {
            home: "", lock: "", isAvailable: false, deepLink: "",
            homeDownload: "", lockDownload: ""
        }
    };

    const current = assets[type] || assets.spring;
    const tapHighlightStyle: CSSProperties = {
        WebkitTapHighlightColor: "transparent",
    };
    const allowContextMenu = (event: MouseEvent<HTMLDivElement>) => {
        if (current.isAvailable) {
            event.stopPropagation()
        }
    }
    const downloadImages = async () => {
        if (!current.isAvailable || !current.lockDownload || !current.homeDownload) {
            return
        }

        const files = await Promise.all([
            getImageFile(current.lockDownload, getImageFileName(type, "lockscreen", current.lockDownload)),
            getImageFile(current.homeDownload, getImageFileName(type, "homescreen", current.homeDownload)),
        ])
        const shareNavigator = navigator as Navigator & {
            canShare?: (data: { files: File[] }) => boolean
            share?: (data: { files: File[]; title?: string }) => Promise<void>
        }

        if (shareNavigator.share && shareNavigator.canShare?.({ files })) {
            try {
                await shareNavigator.share({
                    files,
                    title: "DODU images",
                })
                return
            } catch (error) {
                if (error instanceof DOMException && error.name === "AbortError") {
                    return
                }
            }
        }

        files.forEach(downloadFile)
    }

    return (
        <div className="relative h-full min-h-0" onContextMenu={allowContextMenu}>
            <div className="flex h-full min-w-0 flex-col gap-2">
                {
                    current.isAvailable === false && current.homeDownload === "" ? null
                        :
                        <>
                            <div className="grid min-h-0 w-full flex-1 grid-cols-2 items-stretch justify-center gap-2 sm:gap-4">
                                <figure className="grid min-h-0 min-w-0 grid-rows-[1fr_auto] gap-2 text-center">
                                    <div className="flex min-h-0 min-w-0 items-center justify-center overflow-hidden rounded-2xl border border-black/5">
                                        {current.lock && (
                                            <img
                                                src={current.lock}
                                                className="h-full w-full object-cover"
                                                alt="LockScreen"
                                            />
                                        )}
                                    </div>
                                    <figcaption className="text-sm font-medium text-black/70">
                                        잠금화면
                                    </figcaption>
                                </figure>

                                <figure className="grid min-h-0 min-w-0 grid-rows-[1fr_auto] gap-2 text-center">
                                    <div className="flex min-h-0 min-w-0 items-center justify-center overflow-hidden rounded-2xl border border-black/5">
                                        {current.home && (
                                            <img
                                                src={current.home}
                                                className="h-full w-full object-cover"
                                                alt="HomeScreen"
                                            />
                                        )}
                                    </div>
                                    <figcaption className="text-sm font-medium text-black/70">
                                        홈화면
                                    </figcaption>
                                </figure>
                            </div>
                            <div className="flex shrink-0 items-center justify-around gap-2 sm:gap-4">
                                <MotionButton
                                    className="flex h-12 min-w-0 flex-1 select-none flex-col items-center justify-center rounded-full bg-[#3ead2a] px-3 py-1 text-white outline-none focus-visible:ring-2 focus-visible:ring-[#3ead2a]/40 md:max-w-44"
                                    style={tapHighlightStyle}
                                    onClick={() => window.open(current.deepLink, "_blank")}
                                    whileHover={{ y: -2, scale: 1.04 }}
                                    whileTap={{ scale: 0.95 }}
                                    transition={{ duration: 0.18, ease: "easeOut" }}
                                >
                                    <span className="whitespace-nowrap text-sm font-semibold leading-tight sm:text-base">테마 다운로드</span>
                                    <span className="whitespace-nowrap text-[12px] font-medium leading-tight sm:text-[13px]">for Galaxy</span>
                                </MotionButton>
                                <MotionButton
                                    className="h-12 min-w-0 flex-1 select-none rounded-full border border-[#3ead2a] bg-white px-3 py-2 text-sm font-semibold 
                                 text-[#3ead2a] outline-none focus-visible:ring-2 focus-visible:ring-[#3ead2a]/40 sm:text-base md:max-w-44 md:text-xl"
                                    style={tapHighlightStyle}
                                    onClick={downloadImages}
                                    whileHover={{ y: -2, scale: 1.04 }}
                                    whileTap={{ scale: 0.95 }}
                                    transition={{ duration: 0.18, ease: "easeOut" }}
                                >
                                    이미지 다운로드
                                </MotionButton>
                            </div>
                        </>
                }
            </div>
        </div>

    )
}

export default DownloadView
